// §6.3 — the house window frame: flat, controls on the right, a 1px border.
//
// Drag and resize go through `kernel/wm/drag`, which writes to the DOM and commits to the store
// once (§5.2). Nothing in this component moves a window through React state.
import { useCallback, useRef } from 'react';
import type { Edge, Win } from '@hangar/shared';
import { Minus, Square, Copy, X } from '@/assets/icons';
import { COPY } from '@/copy';
import { useKernel } from '@/kernel/react';
import { MOVE, RESIZE_EDGES, beginDrag, resizeFrom, type DragSession } from '@/kernel/wm/drag';
import { WindowMenuBar } from '../../menubar';
import { housePolicy } from '../policy';

export function WindowFrame({ win }: { win: Win }) {
  const kernel = useKernel();
  const session = useRef<DragSession | null>(null);

  const startDrag = useCallback(
    (event: React.PointerEvent<HTMLElement>, edge: Edge | null) => {
      if (event.button !== 0) return;
      const host = event.currentTarget.closest<HTMLElement>('.win-host');
      if (!host) return;

      const current = kernel.wm.get(win.id);
      if (!current) return;
      if (edge !== null && !current.resizable) return;

      kernel.wm.focus(win.id);
      event.preventDefault();

      const kind = edge === null ? MOVE : resizeFrom(edge);
      // §5.4 — only a *move* can snap; a resize is the user saying what size they want.
      if (edge === null) kernel.snap.begin(win.id);
      session.current = beginDrag({
        element: host,
        capture: event.currentTarget,
        start: current.rect,
        origin: { clientX: event.clientX, clientY: event.clientY },
        pointerId: event.pointerId,
        kind,
        min: { w: current.minW, h: current.minH },
        // §5.2 (D-438) — passed for a resize as well as a move. A move is held wholly inside the
        // work area; a resize only has its top and left edges held there, so a window can still be
        // made larger than the screen without its title bar ending up above it.
        bounds: kernel.workArea(),
        onCommit: (rect) => {
          session.current = null;
          if (edge !== null) {
            kernel.wm.resize(win.id, rect);
            return;
          }
          // The snap engine takes the commit when a zone is armed, so the window lands in the
          // zone rather than where the pointer left it (§5.4).
          if (!kernel.snap.commit()) kernel.wm.setRect(win.id, rect);
        },
        onCancel: () => kernel.snap.cancel(),
      });
    },
    [kernel, win.id],
  );

  const onPointerMove = (event: React.PointerEvent) => {
    if (!session.current) return;
    session.current.pointerMove(event.nativeEvent);
    // The *pointer* decides the zone, not the window's rect — a window confined to the work area
    // never reaches the edge its own pointer is over. The overlay is written directly (§5.2).
    kernel.snap.update({ x: event.clientX, y: event.clientY });
  };
  const onPointerUp = (event: React.PointerEvent) => session.current?.pointerUp(event.nativeEvent);
  const onPointerCancel = () => {
    kernel.snap.cancel();
    session.current?.cancel();
  };

  const onMaximize = () => {
    const current = kernel.wm.get(win.id);
    if (!current) return;
    if (current.state === 'maximized' || current.state === 'snapped') kernel.wm.restore(win.id);
    else if (housePolicy.onMaximizeButton(current) === 'maximize') {
      kernel.wm.maximize(win.id, kernel.workArea());
    }
  };

  const restoring = win.state === 'maximized' || win.state === 'snapped';

  return (
    <div className="house-frame" data-testid={`window-frame-${win.id}`}>
      <div
        className="house-titlebar"
        data-testid={`titlebar-${win.id}`}
        onPointerDown={(e) => startDrag(e, null)}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onDoubleClick={onMaximize}
      >
        <span className="house-title" title={win.title}>
          {win.title}
        </span>
        {win.dirty ? <span className="house-dirty" data-testid="window-dirty" aria-label={COPY.chrome.unsaved} /> : null}
        {win.busy ? <span className="house-busy" data-testid="window-busy" aria-label={COPY.chrome.busy} /> : null}

        <div className="house-controls">
          <button
            type="button"
            className="house-control"
            aria-label={COPY.chrome.minimize}
            data-testid={`window-minimize-${win.id}`}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => kernel.wm.minimize(win.id)}
          >
            <Minus size={14} aria-hidden />
          </button>
          <button
            type="button"
            className="house-control"
            aria-label={restoring ? COPY.chrome.restore : COPY.chrome.maximize}
            data-testid={`window-maximize-${win.id}`}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={onMaximize}
          >
            {restoring ? <Copy size={13} aria-hidden /> : <Square size={12} aria-hidden />}
          </button>
          <button
            type="button"
            className="house-control house-control--close"
            aria-label={COPY.chrome.close}
            data-testid={`window-close-${win.id}`}
            onPointerDown={(e) => e.stopPropagation()}
            // §18 F9 — the user asking is what makes this vetoable; `wm.close` is not.
            onClick={() => void kernel.requestClose(win.id)}
          >
            <X size={14} aria-hidden />
          </button>
        </div>
      </div>

      {/* §6.3 — in-window menu bar, under the title bar. Draws nothing until the app publishes. */}
      <WindowMenuBar win={win} />

      {win.resizable && win.state === 'normal' ? (
        <div className="win-resize-handles" aria-hidden>
          {RESIZE_EDGES.map((edge) => (
            <div
              key={edge}
              className="win-resize-handle"
              data-edge={edge}
              data-testid={`resize-${edge}-${win.id}`}
              onPointerDown={(e) => startDrag(e, edge)}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerCancel}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
